import m, {Vnode} from "mithril";
import ICustomFormSettings from "../ICustomFormSettings";
import {TextboxItem} from "../../model/items/textbox";
import {CheckboxItem} from "../../model/items/checkbox";
import BasePriceTest from "./BasePriceTest";

export default class PriceTestDan2 extends BasePriceTest {
    protected readonly progress;

    private readonly rotation1 = Math.random() > 0.5;
    private readonly rotation2 = Math.random() > 0.5;

    private readonly ci_vpausal_dtb: TextboxItem;
    private readonly ci_current: TextboxItem;
    private readonly ci_current_trzby: TextboxItem;
    private readonly ci_stepUp: TextboxItem;
    private readonly ci_stepDown: TextboxItem;
    private readonly ci_teorMin: TextboxItem;
    private readonly ci_teorMax: TextboxItem;
    private readonly ci_progress: TextboxItem;
    private readonly ci_trzby_kontr: TextboxItem;

    private readonly ci_finished: CheckboxItem;
    private readonly ci_finished_min: CheckboxItem;
    private readonly ci_finished_max: CheckboxItem;
    private readonly ci_finished_dk: CheckboxItem;

    private direction = 'down';

    constructor(settings: ICustomFormSettings) {
        super(settings);

        this.ci_vpausal_dtb = <TextboxItem>settings.form.grid.layout[0][1]?.cellItems.filter(ci => ci instanceof TextboxItem)[0];
        this.ci_current = <TextboxItem>settings.form.grid.layout[1][1]?.cellItems.filter(ci => ci instanceof TextboxItem)[0];
        this.ci_current_trzby = <TextboxItem>settings.form.grid.layout[2][1]?.cellItems.filter(ci => ci instanceof TextboxItem)[0];
        this.ci_stepUp = <TextboxItem>settings.form.grid.layout[3][1]?.cellItems.filter(ci => ci instanceof TextboxItem)[0];
        this.ci_stepDown = <TextboxItem>settings.form.grid.layout[4][1]?.cellItems.filter(ci => ci instanceof TextboxItem)[0];
        this.ci_teorMin = <TextboxItem>settings.form.grid.layout[5][1]?.cellItems.filter(ci => ci instanceof TextboxItem)[0];
        this.ci_teorMax  = <TextboxItem>settings.form.grid.layout[6][1]?.cellItems.filter(ci => ci instanceof TextboxItem)[0];
        this.ci_progress = <TextboxItem>settings.form.grid.layout[7][1]?.cellItems.filter(ci => ci instanceof TextboxItem)[0];
        this.ci_trzby_kontr = <TextboxItem>settings.form.grid.layout[8][1]?.cellItems.filter(ci => ci instanceof TextboxItem)[0];

        this.ci_finished = <CheckboxItem>settings.form.grid.layout[9][0]?.cellItems.filter(ci => ci instanceof CheckboxItem)[0];
        this.ci_finished_max = <CheckboxItem>settings.form.grid.layout[10][0]?.cellItems.filter(ci => ci instanceof CheckboxItem)[0];
        this.ci_finished_min = <CheckboxItem>settings.form.grid.layout[11][0]?.cellItems.filter(ci => ci instanceof CheckboxItem)[0];
        this.ci_finished_dk = <CheckboxItem>settings.form.grid.layout[12][0]?.cellItems.filter(ci => ci instanceof CheckboxItem)[0];

        this.progress = JSON.parse(this.ci_progress.getVariable() || '[]');
    }

    checkRequirements(): boolean {
        return true;
    }

    protected validateAndPossiblyNextForm() {
        const current = this.ci_current.getVariable();
        if (current && this.prices.filter(p => p == current).length >= 1) {
            this.ci_finished.setVariable(1);
            this.settings.nextForm();
        }

        const teorMin = Number(this.ci_teorMin.getVariable());
        if (current < teorMin) {
            this.ci_finished.setVariable(1);
            this.ci_finished_min.setVariable(1);
            this.settings.nextForm();
        }

        const teorMax = Number(this.ci_teorMax.getVariable());
        if (current > teorMax) {
            this.ci_finished.setVariable(1);
            this.ci_finished_max.setVariable(1);
            this.settings.nextForm();
        }
    }

    protected setPrice(value: number, option: number) {
        const oldCurrent = this.ci_current.getVariable();
        const oldTrzby = this.ci_current_trzby.getVariable();
        this.progress.push([oldCurrent,oldTrzby,option]);

        this.ci_current.set(value);
        const trzby = value * this.ci_trzby_kontr.getVariable() / 100;
        this.ci_current_trzby.set(trzby);
        this.ci_progress.set(JSON.stringify(this.progress));
    }

    protected stepUp(option: number): void {
        this.makeAppearRight();
        const step = Number(this.ci_stepUp.getVariable());
        const teor = Number(this.ci_teorMax.getVariable());
        const current = Number(this.ci_current.getVariable());
        const newPrice = Math.min(current+step, teor);

        this.direction = 'up';

        this.setPrice(newPrice, option);
        this.validateAndPossiblyNextForm();
    }

    protected stepDown(option: number): void {
        this.makeAppearRight();
        const step = Number(this.ci_stepDown.getVariable());
        const teor = Number(this.ci_teorMin.getVariable());
        const current = Number(this.ci_current.getVariable());
        const newPrice = Math.max(current-step, teor);

        this.direction = 'down';

        this.setPrice(newPrice, option);
        this.validateAndPossiblyNextForm();
    }

    protected stepDK(option = 4) {
        this.makeAppearRight();
        this.setPrice(this.ci_current.getVariable(), option);
        this.ci_finished.setVariable(1);
        this.ci_finished_dk.setVariable(1);
        this.settings.nextForm();
    }

    protected viewValue(): Vnode|null {
        //m('.PriceTest-value', {}, this.ci_current.getVariable()),
        return null;
    }

    protected viewQuestion(): Vnode {
        return m('.PriceTest-question', {}, [
            m.trust(`A teď si představte, že by neexistovala možnost danit příjmy paušální daní, vybral/a byste si výdajový paušál <b>${this.ci_current.getVariable()} %</b> (Vaše uznatelné náklady by byly <b>${this.ci_current_trzby.getVariable()} Kč</b>, nebo evidenci reálných výdajů?`)
        ]);
    }

    protected viewOptions(): Vnode {
        const down = [
            m('button.btn.large.info.outline', {
                onclick: () => this.stepDown(1),
            }, m.trust('<b>Výdajový paušál</b>, protože by se mi <u>finančně vyplatil</u>')),
            m('button.btn.large.info.outline', {
                onclick: () => this.direction === 'down' ? this.stepDown(2) : this.stepUp(2),
            }, m.trust('<b>Výdajový paušál</b>, sice by se mi takový paušál finančně nevyplatil, ale nechtěl/a bych <u>řešit administrativu</u> (související s daňovou evidencí)')),
        ];

        const up = [
            m('button.btn.large.info.outline', {
                onclick: () => this.stepUp(3),
            }, m.trust('<b>Evidenci reálných výdajů</b> doložitelných pomocí <u>daňové evidence</u>')),
        ];

        const dk = [
            m('button.btn.large.info.outline', {
                onclick: () => this.direction === 'down' ? this.stepDown(4) : this.stepUp(4),
            }, m.trust('Nevím')),
        ];

        if (this.rotation1) {
            down.reverse();
        }

        const options = this.rotation2 ? [...up, ...down, dk] : [...down, ...up, dk]

        return m('.PriceTest-controls', {}, options)
    }

    protected getQuestion(): string {
        return `<b>A teď si představte, že by neexistovala možnost danit příjmy paušální daní, vybral/a byste si výdajový paušál  <span class="ctHighlight">${this.ci_current.getVariable()}&nbsp;%</span> (Vaše uznatelné náklady by byly <span class="ctHighlight">${this.ci_current_trzby.getVariable()}&nbsp;Kč</span>), nebo evidenci reálných výdajů?</b><br/>(předpokládejte, že vše ostatní vč. výše Vašich aktuálních příjmů by se nezměnilo)<div class="ctHint">Rozhodnutí spočívá v tom, <u>jakou částku odečíst</u> od příjmů a tím získat <u>zisk</u>.<br/><br/><u>Daňová evidence</u> obnáší vedení účetních knih, včetně knihy příjmů a výdajů (výdaje, příjmy, účetnictví smluv, apod.), a také dodržování pravidel o archivaci dokumentů.</div>`;
    }
}
